import React, { useEffect, useState } from "react";

/**
 * GoogleAuthCallback
 *
 * Purpose:
 * - Handle the OAuth redirect that includes ?code=... (or ?error=...)
 * - POST the code to backend endpoint `/google/auth/callback` (see FRONTEND_INTEGRATION_GUIDE)
 * - If opened as a popup, postMessage result back to window.opener and then close the popup
 * - If opened in the same tab, redirect the user into the app (HashRouter) after processing
 *
 * Notes:
 * - The backend endpoint is expected to exchange the code with Google and persist tokens.
 * - For security, replace the postMessage target origin ("*") with a specific origin when possible.
 */

export default function GoogleAuthCallback() {
  const [status, setStatus] = useState("Processing Google authorization...");

  useEffect(() => {
    let mounted = true;

  let callbackEmail = null;

  async function handleCallback() {
      try {
        // Extract params from either the query string or the hash (robust fallback).
        const qs = new URLSearchParams(window.location.search || "");
        let code = qs.get("code");
        let error = qs.get("error");
        let state = qs.get("state");

        // If not found in search, attempt to parse from hash (e.g. #/?code=... or #/callback?code=...)
        if (!code && window.location.hash) {
          try {
            // Strip leading '#'
            const hash = window.location.hash.replace(/^#/, "");
            // If hash contains a '?', take the part after '?' as querystring
            const qIndex = hash.indexOf("?");
            const hashQs = qIndex !== -1 ? hash.substring(qIndex + 1) : hash;
            const fromHash = new URLSearchParams(hashQs);
            code = fromHash.get("code") || code;
            error = fromHash.get("error") || error;
            state = fromHash.get("state") || state;
            // Also try common token keys
            if (!code) code = fromHash.get("auth_code") || fromHash.get("authorization_code");
          } catch (e) {
            // ignore parsing errors
            // (we'll handle the absence of a code below)
          }
        }

        // Optional clinic/account email passed through the auth URL
        callbackEmail =
          qs.get("email") || qs.get("clinicEmail") || qs.get("accountEmail");
        // prepare clinicId variable and then attempt to read pending info from localStorage
        let clinicId = null;
        try {
          const pending = JSON.parse(localStorage.getItem("google_oauth_pending") || "null");
          if (pending && !callbackEmail) {
            callbackEmail = pending.email || callbackEmail;
          }
          // prefer clinicId from pending if available
          if (pending && pending.clinicId) {
            clinicId = pending.clinicId;
          }
        } catch (e) {
          // ignore
        }
        if (!callbackEmail && window.location.hash) {
          try {
            const hash = window.location.hash.replace(/^#/, "");
            const qIndex = hash.indexOf("?");
            const hashQs = qIndex !== -1 ? hash.substring(qIndex + 1) : hash;
            const fromHash = new URLSearchParams(hashQs);
            callbackEmail =
              fromHash.get("email") || fromHash.get("clinicEmail") || fromHash.get("accountEmail");
          } catch (e) {
            // ignore
          }
        }

        if (error) {
          const msg = `Authorization error: ${error}`;
          if (mounted) setStatus(msg);
          // mark failed in localStorage
          try {
            const p = JSON.parse(localStorage.getItem("google_oauth_pending") || "null");
            if (p) {
              p.status = "error";
              p.error = error;
              p.ts = Date.now();
              localStorage.setItem("google_oauth_pending", JSON.stringify(p));
            }
          } catch (e) {}
          notifyOpener({ type: "GOOGLE_AUTH_ERROR", payload: { error, state, email: callbackEmail } });
          return;
        }

        if (!code) {
          const msg = "No authorization code found in the URL (tried query and hash).";
          if (mounted) setStatus(msg);
          // mark failed in localStorage
          try {
            const p = JSON.parse(localStorage.getItem("google_oauth_pending") || "null");
            if (p) {
              p.status = "error";
              p.error = "no_code";
              p.ts = Date.now();
              localStorage.setItem("google_oauth_pending", JSON.stringify(p));
            }
          } catch (e) {}
          notifyOpener({ type: "GOOGLE_AUTH_ERROR", payload: { error: "no_code", state, email: callbackEmail } });
          return;
        }

        if (mounted) setStatus("Exchanging authorization code with backend...");

        // POST the code to the external backend endpoint and include the code in the JSON body.
        const backendBase = (import.meta.env.VITE_APP_API_BASE_URL || "").replace(/\/+$/, ""); // remove trailing slash
        const externalUrl = `${backendBase}/api/google/auth/callback`;

        // include clinicId if present in localStorage pending entry (already read above)
        try {
          const pending = JSON.parse(localStorage.getItem("google_oauth_pending") || "null");
          if (pending && pending.clinicId) clinicId = pending.clinicId;
        } catch (e) {
          // ignore
        }

        const res = await fetch(externalUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          mode: "cors",
          body: JSON.stringify({ code, state, email: callbackEmail, clinicId }),
        });

        let body = {};
        try {
          body = await res.json();
        } catch (e) {
          // If backend returns non-JSON, keep body as {}
        }

        if (!res.ok) {
          const errMsg = body?.error || body?.message || `HTTP ${res.status}`;
          if (mounted) setStatus(`Backend error: ${errMsg}`);
          // mark failed in localStorage
          try {
            const p = JSON.parse(localStorage.getItem("google_oauth_pending") || "null");
            if (p) {
              p.status = "error";
              p.error = errMsg;
              p.ts = Date.now();
              localStorage.setItem("google_oauth_pending", JSON.stringify(p));
            }
          } catch (e) {}

          notifyOpener({ type: "GOOGLE_AUTH_ERROR", payload: { error: errMsg, state, email: callbackEmail, clinicId } });
          return;
        }

        // Success path: backend indicates token saved / refresh token presence etc.
        const successMsg = body?.message || "Google account connected successfully.";
        if (mounted) setStatus(successMsg);

        // update localStorage marker to connected
        try {
          const p = JSON.parse(localStorage.getItem("google_oauth_pending") || "null");
          if (p) {
            p.status = "connected";
            p.ts = Date.now();
            p.backend = body || null;
            localStorage.setItem("google_oauth_pending", JSON.stringify(p));
          }
        } catch (e) {}

        notifyOpener({ type: "GOOGLE_AUTH_CODE", payload: { code, state, email: callbackEmail, clinicId, backend: body } });
      } catch (err) {
        const errMsg = err?.message || String(err);
  if (mounted) setStatus(`Unexpected error: ${errMsg}`);
        try {
          const p = JSON.parse(localStorage.getItem("google_oauth_pending") || "null");
          if (p) {
            p.status = "error";
            p.error = errMsg;
            p.ts = Date.now();
            localStorage.setItem("google_oauth_pending", JSON.stringify(p));
          }
        } catch (e) {}

        notifyOpener({ type: "GOOGLE_AUTH_ERROR", payload: { error: errMsg, email: callbackEmail } });
      }
    }

    function notifyOpener(message) {
      try {
        // If there is an opener window (popup flow), postMessage and then close
        if (window.opener && !window.opener.closed) {
          // Determine a safe targetOrigin. If we can read the opener's origin, use it;
          // otherwise fall back to '*'. Accessing opener.location may throw if cross-origin.
          let targetOrigin = "*";
          try {
            if (window.opener && window.opener.location && window.opener.location.origin) {
              targetOrigin = window.opener.location.origin;
            }
          } catch (e) {
            // ignore cross-origin read errors and keep targetOrigin as '*'
            console.debug("GoogleAuthCallback: could not read opener origin (cross-origin) - using '*' for postMessage target", e);
          }

          console.debug("GoogleAuthCallback: posting message to opener", { message, targetOrigin });
          window.opener.postMessage(
            {
              source: "google-auth-callback",
              ...message,
            },
            targetOrigin,
          );

          // Give the opener a short time to receive the message before closing.
          setTimeout(() => {
            try {
              window.close();
            } catch (e) {
              // ignore
            }
          }, 250);
        } else {
          // Not a popup: redirect into the HashRouter app (use hash route)
          // HashRouter expects something like http://host/#/
          const fallbackHash = "/#/admin/clinics-list";
          const origin = window.location.origin || "";
          setTimeout(() => {
            window.location.href = `${origin}${fallbackHash}`;
          }, 900);
        }
      } catch (e) {
        // Fallback to app landing if anything goes wrong
        const origin = window.location.origin || "";
        window.location.href = `${origin}/#/admin/clinics-list`;
      }
    }

    handleCallback();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ padding: 20 }}>
      <h2>Google Authorization</h2>
      <p>{status}</p>
      <small>
        If this page does not automatically close, you can safely close this
        window and return to the application.
      </small>
    </div>
  );
}
